// Social handling and sharing functionality
//
// CHANGE LOG (newest first)
// 2026-10-10 (later) — Claude Code (Opus 5.5), at Bob's direction: the 👤 row
// now previews on hover and stays open (pinned) on click, instead of needing a
// click to see it at all. The tooltip is dropped since hovering shows the icons.
// 2026-10-10 — Claude Code (Opus 5.5), at Bob's direction: removed the legacy
// per-network share row (Shift+click on the share button) and its Mastodon
// server prompt. The browser's share sheet is now the only share path; the
// presence row ("my presence" -- links to Bob's own accounts) stays.

/** Grace period for moving the pointer from the toggle button down into its row */
const HoverLeaveDelayMs = 300;

/**
 * Rows opened by a `.social-toggle` button (its data-target names the row).
 * Hovering the button previews the row; clicking pins it open until clicked
 * again, a click lands elsewhere, or Escape. Touch has no hover, so there a
 * tap simply pins.
 */
export function initSocialCompact() {
    const toggles = Array.from(document.querySelectorAll<HTMLButtonElement>('.social-toggle'));
    const rowOf = (btn: HTMLButtonElement) => document.getElementById(btn.dataset.target);

    let pinned: HTMLButtonElement = null;
    let leaveTimer: number = null;

    const show = (btn: HTMLButtonElement) => rowOf(btn)?.classList.remove('hide');
    const hide = (btn: HTMLButtonElement) => {
        rowOf(btn)?.classList.add('hide');
        btn.classList.remove('active');
    };
    const unpinAll = () => {
        pinned = null;
        toggles.forEach(hide);
    };
    const cancelLeave = () => {
        if (leaveTimer) clearTimeout(leaveTimer);
        leaveTimer = null;
    };

    for (const btn of toggles) {
        const row = rowOf(btn);
        if (!row)
            continue;

        // Preview: entering the button or its row shows it; leaving both hides
        // it after a short grace, unless the click has pinned it
        const enter = () => {
            cancelLeave();
            if (pinned && pinned !== btn) return;   // Another row is pinned open
            show(btn);
        };
        const leave = () => {
            cancelLeave();
            leaveTimer = window.setTimeout(() => {
                if (pinned !== btn) hide(btn);
            }, HoverLeaveDelayMs);
        };
        btn.addEventListener('mouseenter', enter);
        btn.addEventListener('mouseleave', leave);
        row.addEventListener('mouseenter', enter);
        row.addEventListener('mouseleave', leave);

        btn.addEventListener('click', (ev: MouseEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            cancelLeave();
            if (pinned === btn) {           // Second click unpins and closes
                unpinAll();
                return;
            }
            unpinAll();
            pinned = btn;
            show(btn);
            btn.classList.add('active');
        });
    }

    // A click outside the compact header closes whatever is pinned
    document.addEventListener('click', (ev: MouseEvent) => {
        if (!(ev.target as HTMLElement).closest('.nav-compact-header'))
            unpinAll();
    });

    document.addEventListener('keydown', (ev: KeyboardEvent) => {
        if (ev.key === 'Escape')
            unpinAll();
    });
}

/**
 * Hand the page to the browser's own share sheet, which already knows which
 * apps the user has and keeps its own list current -- so there is nothing here
 * per network. Falls back to copying the link where the Web Share API is
 * missing (desktop Firefox, older browsers).
 */
export function initShare() {
    const button = document.getElementById('share-button') as HTMLButtonElement;
    if (!button)
        return;

    const canShare = typeof navigator.share === 'function';
    const canCopy = Boolean(navigator.clipboard);

    // Name the page rather than saying "this page" -- the title is only known
    // once the article has loaded, so build the label on demand
    const pageName = () => document.title || location.href;
    const relabel = () => {
        button.title = canShare ? `Share "${pageName()}"`
            : canCopy ? `Copy link to "${pageName()}"`
                : `This browser cannot share or copy "${pageName()}"`;
    };
    relabel();
    button.addEventListener('mouseenter', relabel);
    button.addEventListener('focus', relabel);

    button.addEventListener('click', async (ev: MouseEvent) => {
        ev.preventDefault();
        ev.stopPropagation();

        const url = location.href;

        if (canShare) {
            try {
                await navigator.share({ title: pageName(), url });
            }
            catch (e: any) {
                // AbortError only means the user dismissed the sheet
                if (e?.name !== 'AbortError')
                    console.error(`Share failed for ${url}`, e);
            }
            return;
        }

        if (!canCopy) {
            // 2026-10-10 Claude Code: this case used to open the legacy row;
            // with that gone, say so rather than doing nothing silently
            console.error(`No share sheet or clipboard in this browser; cannot share ${url}`);
            return;
        }

        try {
            await navigator.clipboard.writeText(url);
            confirmCopy(button);
        }
        catch (e: any) {
            console.error(`Could not copy ${url} to the clipboard`, e);
        }
    });
}

// Briefly swap the icon so a copy is visible without needing a tooltip
function confirmCopy(button: HTMLButtonElement) {
    const icon = button.querySelector('.social-icon');
    if (!icon)
        return;
    const original = icon.textContent;
    icon.textContent = '✓';
    setTimeout(() => { icon.textContent = original; }, 1500);
}
